import { UnknownKeysParam, z, ZodError } from 'zod';

import { notifyError, notifySuccess } from 'utils/notify/notify.utils';

import { toBoolean } from './to-boolean';

interface IRequestWithZodSchemaOptions {
  notifyCbResolveString?: string;
  notifyCbRejectString?: string;
}

/**
 * Makes a request and then validates the response with a given Zod schema.
 * If the response is valid, it returns the `data` property of the parsed response.
 * If the response is invalid, it throws a ZodError.
 *
 * @param requestFn - a function that returns a Promise of the request response
 * @param schema - a Zod schema to validate the response with
 * @param options - an object with optional `notifyCbResolveString` and `notifyCbRejectString` properties
 * @param notifyCbString - an optional string to notify the user
 * @returns a Promise that resolves to the `data` property of the parsed response
 * @throws a ZodError if the response is invalid
 */
export const requestWithZodSchema = async <Output extends { data: unknown }>(
  requestFn: () => Promise<unknown>,
  schema:
    | z.ZodObject<Record<string, z.ZodTypeAny>, UnknownKeysParam, z.ZodTypeAny, Output>
    | z.ZodEffects<z.ZodTypeAny, Output, unknown>,
  options: IRequestWithZodSchemaOptions = {},
): Promise<Output['data']> => {
  const { notifyCbResolveString, notifyCbRejectString } = options;
  const requestFnName = requestFn.name;

  return await requestFn()
    .then((response) => {
      notifyCbResolveString && notifySuccess(notifyCbResolveString);
      return schema.parse(response).data;
    })
    .catch((error: unknown) => {
      if (error instanceof ZodError && toBoolean(process.env.REACT_APP_DEBUG)) {
        console.error(requestFnName, error);
      }
      notifyCbRejectString && notifyError(notifyCbRejectString);
      throw error;
    });
};
